import instance from "~/v2/api";
import { looseParse } from "~/v2/api/looseParse";
import { type RiskControls, riskControlsSchema } from "./riskControls.schema";

/** The two account risk controls trading-api implements against gobroker. */
export type RiskControlKey = "suspendTrading" | "liquidationOnly";

export const riskControlsQueryKeys = {
  all: ["v3-poc", "risk-controls"] as const,
  detail: (accountID: string) =>
    [...riskControlsQueryKeys.all, accountID] as const,
};

// Live only: every operation is declared `resource:account?env=live` and there
// is no `/paper_accounts` variant, so a paper account id 404s here by design.
const riskControlsPath = (accountID: string) =>
  `/accounts/${accountID}/risk-controls`;

const CONTROL_SEGMENT: Record<RiskControlKey, string> = {
  suspendTrading: "suspend-trading",
  liquidationOnly: "liquidation-only",
};

export const getRiskControls = async (
  accountID: string,
): Promise<RiskControls> => {
  const response = await instance.get(riskControlsPath(accountID));

  return looseParse(riskControlsSchema, response.data, "v3.getRiskControls");
};

export interface SetRiskControlParams {
  accountID: string;
  control: RiskControlKey;
  enabled: boolean;
}

/**
 * Returns the state read back from gobroker, not the requested value — the
 * PATCH answers 204, so the read-back is the only trustworthy account of what
 * was stored. Callers should seed the cache from the result.
 */
export const setRiskControl = async ({
  accountID,
  control,
  enabled,
}: SetRiskControlParams): Promise<RiskControls> => {
  const response = await instance.put(
    `${riskControlsPath(accountID)}/${CONTROL_SEGMENT[control]}`,
    { enabled },
    // Accepted by the contract; trading-api drops it until PRIME-1143 task D lands.
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );

  return looseParse(riskControlsSchema, response.data, "v3.setRiskControl");
};
